import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import Logo from "@/components/brand/Logo";
import { reportPdfFailure } from "@/sentry/reporters";

const PIE_COLORS = ["#C5A059", "#0A1128", "#9CAE96", "#D4B574"];

export default function ReportsPage() {
  const { activeProjectId, activeProject } = useProjects();
  const [report, setReport] = useState(null);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    if (!activeProjectId) return;
    api.get(`/reports/${activeProjectId}/latest`).then(({ data }) => setReport(data));
    api.get(`/sales/${activeProjectId}`).then(({ data }) => setSales(data));
  }, [activeProjectId]);

  if (!report) return <DashboardLayout><p className="text-muted-foreground">Generating report…</p></DashboardLayout>;

  const shareUrl = `${window.location.origin}/r/${activeProjectId}`;
  const monthLabel = new Date(report.generated_at).toLocaleString("en-US", { month: "long", year: "numeric" });
  const term = report.terminology || {};

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <p className="editorial-overline">Monthly growth report</p>
          <h1 className="font-serif text-3xl text-navy-900 mt-1">{monthLabel}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Public link copied"); }}
            data-testid="report-share-btn"
            className="px-4 py-2 border border-border hover:border-gold-500 rounded-sm text-sm bg-white"
          >
            Copy public link
          </button>
          <button
            onClick={async () => {
              try {
                const resp = await api.get(`/reports/${activeProjectId}/pdf`, { responseType: "blob" });
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const name = (activeProject?.name || "report").toLowerCase().replace(/[^a-z0-9-]+/g, "-").slice(0, 60);
                a.download = `trendrise-${name}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                reportPdfFailure(err, { project_id: activeProjectId, workflow: "pdf_download" });
                toast.error("PDF export failed. Please try again.");
              }
            }}
            data-testid="report-pdf-btn"
            className="px-4 py-2 bg-navy-900 hover:bg-navy-800 text-ivory-50 font-semibold rounded-sm text-sm"
          >
            Export PDF
          </button>
        </div>
      </div>

      <div className="bg-navy-900 text-ivory-50 rounded-sm overflow-hidden relative" data-testid="report-cover">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gold-500" />
        <div className="px-8 sm:px-12 py-12 sm:py-16 grid sm:grid-cols-2 gap-8 items-center">
          <div>
            <Logo variant="light" size={36} />
            <p className="editorial-overline mt-8">Prepared for</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight">{activeProject?.name || "Your project"}</h2>
            <p className="text-ivory-200/70 mt-2 font-mono text-xs">{activeProject?.website_url}</p>
            <p className="editorial-overline mt-8">Reporting period</p>
            <p className="font-serif text-2xl mt-2">{monthLabel}</p>
          </div>
          <div className="border-l border-ivory-200/15 pl-8 hidden sm:block">
            <p className="editorial-overline">Headline number</p>
            <p className="font-serif text-6xl text-gold-500 mt-2">+24%</p>
            <p className="text-sm text-ivory-200/70 mt-1">organic sessions vs prior 30 days</p>
            <div className="gold-divider mt-6 opacity-60" />
            <p className="text-sm text-ivory-200/80 mt-6 italic leading-relaxed">
              &ldquo;Compounding starts with one published page every week, with conversion blocks already wired into the brief.&rdquo;
            </p>
          </div>
        </div>
      </div>

      <Section title="01 — Executive summary">
        <p className="font-serif text-xl leading-relaxed text-navy-900">{report.executive_summary}</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <Stat label="Content shipped" value={report.content_production} />
          <Stat label="Keyword clusters" value={report.keyword_wins} />
          <Stat label="AI visibility avg" value={`${report.ai_visibility_avg}/100`} />
        </div>
      </Section>

      <Section title="02 — Traffic">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={report.traffic_trend}>
              <defs>
                <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C5A059" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E0D5" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Area type="monotone" dataKey="views" stroke="#C5A059" fill="url(#rg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {sales && (
        <Section title="03 — Revenue attribution">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sales.revenue_by_source} dataKey="revenue" nameKey="source" innerRadius={50} outerRadius={100} paddingAngle={2} stroke="#FFFFFF">
                    {sales.revenue_by_source.map((entry) => (
                      <Cell key={entry.source} fill={PIE_COLORS[sales.revenue_by_source.indexOf(entry) % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="font-serif text-xl leading-relaxed">
                {term.revenue_recurring_label || "Recurring revenue"}: <span className="text-gold-600">${(report.revenue_impact?.recurring || 0).toLocaleString()}</span>
              </p>
              <p className="font-serif text-xl leading-relaxed mt-2">
                {term.revenue_one_time_label || "One-time revenue"}: <span className="text-gold-600">${(report.revenue_impact?.one_time || 0).toLocaleString()}</span>
              </p>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                Content surfaces continue to be the highest-leverage source. Comparison and category pages are emerging as secondary drivers — recommend doubling down in the next 30 days.
              </p>
            </div>
          </div>
        </Section>
      )}

      {sales && (
        <Section title="04 — Content production">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sales.series.slice(-14).map((s) => ({ date: s.date.slice(5), pieces: s.content_published || 0 }))}>
                <CartesianGrid stroke="#E5E0D5" vertical={false} />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="pieces" fill="#0A1128" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      <Section title="05 — Technical fixes">
        <p className="text-sm text-muted-foreground">
          Open issues this period: <span className="font-medium text-navy-900">{report.technical_fixes_pending}</span>.
          Highest-impact items relate to schema coverage and page depth — both are in pipeline this month.
        </p>
      </Section>

      <Section title="06 — Next recommended actions">
        <ul className="space-y-3">
          {report.next_actions.map((a) => (
            <li key={a} className="flex gap-3 border-b border-border pb-3 last:border-0" data-testid={`report-action-${a.slice(0, 12)}`}>
              <span className="font-serif text-gold-500 text-lg leading-none mt-0.5">▸</span>
              <span className="text-sm text-navy-900">{a}</span>
            </li>
          ))}
        </ul>
      </Section>

      <div className="bg-white border border-border rounded-sm p-6 mt-5 flex items-center justify-between text-sm" data-testid="report-signature">
        <div>
          <p className="editorial-overline">Generated by</p>
          <div className="mt-2"><Logo variant="dark" size={22} /></div>
        </div>
        <p className="text-muted-foreground text-xs italic">{new Date(report.generated_at).toLocaleString()}</p>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-border rounded-sm p-7 sm:p-8 mt-5" data-testid={`report-section-${title.split(" ")[0]}`}>
      <p className="editorial-overline">{title}</p>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-gold-500 pl-4">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-serif text-3xl mt-1">{value}</p>
    </div>
  );
}
